/**
 * Invoice Preview Component (T090)
 * Formatted invoice preview for display in modal or full-page view
 */

import { FileText, Calendar, User, Mail, Phone } from 'lucide-react';

interface LineItem {
  id?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount?: number;
}

interface InvoicePreviewData {
  invoiceNumber?: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  issueDate?: string;
  dueDate: string;
  lineItems: LineItem[];
  subtotal: number;
  taxRate: number;
  tax: number;
  total: number;
  notes?: string;
  status?: string;
  taxExempt?: boolean;
}

interface InvoicePreviewProps {
  invoice: InvoicePreviewData;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export default function InvoicePreview({ invoice }: InvoicePreviewProps) {
  return (
    <div className="space-y-6 print:text-black">
      {/* Business Header */}
      <div className="flex items-start justify-between border-b border-border pb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold tracking-tight">INVOICE</h2>
          </div>
          <p className="text-sm text-muted-foreground">Ishefa Healing</p>
        </div>
        <div className="text-right space-y-1">
          {invoice.invoiceNumber && (
            <p className="text-lg font-semibold">{invoice.invoiceNumber}</p>
          )}
          {invoice.status && (
            <span className="text-sm font-medium text-muted-foreground uppercase">
              {invoice.status}
            </span>
          )}
        </div>
      </div>

      {/* Customer & Dates */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Bill To
          </p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="font-semibold">{invoice.customerName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{invoice.customerEmail}</span>
            </div>
            {invoice.customerPhone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{invoice.customerPhone}</span>
              </div>
            )}
            {invoice.taxExempt && (
              <span className="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded bg-warning/10 text-warning">
                Tax Exempt
              </span>
            )}
          </div>
        </div>
        <div className="space-y-3 text-right">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Details
          </p>
          <div className="space-y-1.5">
            {invoice.issueDate && (
              <div className="flex items-center justify-end gap-2">
                <span className="text-sm text-muted-foreground">Issue Date:</span>
                <span className="text-sm font-medium">
                  {new Date(invoice.issueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Due:</span>
              <span className="text-sm font-medium">
                {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left px-4 py-3 font-semibold">Description</th>
              <th className="text-right px-4 py-3 font-semibold w-20">Qty</th>
              <th className="text-right px-4 py-3 font-semibold w-28">Unit Price</th>
              <th className="text-right px-4 py-3 font-semibold w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.map((item, index) => {
              const amount = item.amount ?? item.quantity * item.unitPrice;
              return (
                <tr key={index} className="border-t border-border">
                  <td className="px-4 py-3">{item.description || '—'}</td>
                  <td className="px-4 py-3 text-right">{item.quantity}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(item.unitPrice)}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(amount)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-64 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              Tax {invoice.taxExempt ? '(Exempt)' : `(${invoice.taxRate}%)`}
            </span>
            <span className="font-medium">{formatCurrency(invoice.tax)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
            <span>Total</span>
            <span>{formatCurrency(invoice.total)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div className="border-t border-border pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Notes
          </p>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{invoice.notes}</p>
        </div>
      )}
    </div>
  );
}
